import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { eventsApi, albumsApi, getFileUrl } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft, Copy, Users, Image, Heart, QrCode, Tv, FolderOpen,
  Camera, ChevronRight, Loader2, Check, BarChart3, Download,
  Power, Film, Sparkles, Lock, Unlock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import PremiumUpgradeModal from "../components/PremiumUpgradeModal";

export default function EventPage() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [ending, setEnding] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoStage, setVideoStage] = useState("");
  const progressTimerRef = useRef(null);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [albumsBlockedOpen, setAlbumsBlockedOpen] = useState(false);
  const [downloadBlockedOpen, setDownloadBlockedOpen] = useState(false);

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  // Auto-open premium upgrade reminder when the host is getting close to the
  // free plan limit (<=5 photos remaining) or has hit it, so they can pay
  // BEFORE guests are blocked. Dismissal is stored per (eventId, remaining)
  // bucket in localStorage so each new threshold still re-opens the reminder.
  useEffect(() => {
    if (!event) return;
    if (event.plan !== "free") return;
    const used = event.media_count || 0;
    const limit = event.free_limit || 30;
    const remaining = Math.max(limit - used, 0);
    if (remaining > 5) return; // Still far from limit
    try {
      const bucketKey = `festa_upgrade_reminder_${eventId}_r${remaining}`;
      if (!localStorage.getItem(bucketKey)) {
        setUpgradeModalOpen(true);
      }
    } catch (_) {
      setUpgradeModalOpen(true);
    }
  }, [event, eventId]);

  const loadEvent = async () => {
    try {
      const [eventRes, statsRes] = await Promise.all([
        eventsApi.get(eventId),
        eventsApi.stats(eventId),
      ]);
      setEvent(eventRes.data);
      setStats(statsRes.data);
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (event?.join_url) {
      navigator.clipboard.writeText(event.join_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEndEvent = async () => {
    setEndDialogOpen(false);
    // Wait for dialog to fully unmount before changing state
    await new Promise((r) => setTimeout(r, 300));
    setEnding(true);
    try {
      await eventsApi.end(eventId);
      await loadEvent();
    } catch (err) {
      console.error(err);
    } finally {
      setEnding(false);
    }
  };

  const handleReactivate = async () => {
    try {
      await eventsApi.reactivate(eventId);
      loadEvent();
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerateVideo = async () => {
    setGeneratingVideo(true);
    setVideoProgress(2);
    // Smooth time-based tween: asymptotic curve towards 95% over ~90s,
    // never reaches 100% until the poll confirms `done` (real signal).
    const startTs = Date.now();
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(() => {
      const elapsedSec = (Date.now() - startTs) / 1000;
      // Curve: progress = 95 * (1 - e^(-t/45))  →  smooth & realistic
      const v = 95 * (1 - Math.exp(-elapsedSec / 45));
      setVideoProgress((cur) => Math.max(cur, Math.min(95, v)));
    }, 250);

    toast.success("Vamos te avisar por email!", {
      description: "O video esta sendo gerado em segundo plano. Voce pode fechar esta aba.",
      duration: 7000,
    });

    try {
      await eventsApi.generateVideo(eventId);

      const MAX_POLL_SECONDS = 12 * 60;
      const INTERVAL_MS = 4000;
      const start = Date.now();
      let done = false;
      let failError = null;

      while (!done && (Date.now() - start) / 1000 < MAX_POLL_SECONDS) {
        await new Promise((r) => setTimeout(r, INTERVAL_MS));
        try {
          const { data } = await eventsApi.videoStatus(eventId);
          if (data?.stage) setVideoStage(data.stage);
          if (data?.status === "done") {
            done = true;
            break;
          }
          if (data?.status === "failed") {
            failError = data?.error || "Falha ao gerar video";
            break;
          }
        } catch (innerErr) {
          if (innerErr?.message && !innerErr?.response) {
            failError = innerErr.message;
            break;
          }
        }
      }

      if (failError) throw new Error(failError);

      if (done) {
        setVideoProgress(100);
        toast.success("Video pronto!", {
          description: "Seu slideshow ja esta disponivel. Clique em assistir abaixo.",
          duration: 5000,
        });
      } else {
        toast.warning("Demorou mais que o esperado", {
          description: "Continuaremos processando — voce recebera um email quando concluir.",
          duration: 6000,
        });
      }
      loadEvent();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Erro ao gerar video";
      toast.error("Nao conseguimos gerar o video", { description: msg });
    } finally {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      // Reset state after a brief moment so the user sees "100%" land.
      setTimeout(() => {
        setGeneratingVideo(false);
        setVideoProgress(0);
        setVideoStage("");
      }, 800);
    }
  };

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF7]" data-testid="event-page">
      {/* Header */}
      <header className="sticky top-0 w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917] z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/dashboard" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917]">
              <Camera className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-tight text-[#1C1917] truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {event?.name}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="w-full bg-white border-2 border-[#1C1917] rounded-xl shadow-[2px_2px_0px_#1C1917] mb-6">
            <TabsTrigger value="overview" className="flex-1 font-bold data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white rounded-lg" data-testid="tab-overview">
              Visao Geral
            </TabsTrigger>
            <TabsTrigger value="qrcode" className="flex-1 font-bold data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white rounded-lg" data-testid="tab-qrcode">
              QR Code
            </TabsTrigger>
            <TabsTrigger value="stats" className="flex-1 font-bold data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white rounded-lg" data-testid="tab-stats">
              Estatisticas
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-4">
              {/* Quick Actions */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Feed ao Vivo", icon: Image, color: "#FF6B6B", action: () => navigate(`/feed/${eventId}`) },
                  { label: "Albums", icon: FolderOpen, color: "#8B5CF6", action: () => {
                      if (event?.plan === "free") {
                        setAlbumsBlockedOpen(true);
                      } else {
                        navigate(`/albums/${eventId}`);
                      }
                    } },
                  { label: "Modo TV", icon: Tv, color: "#1C1917", action: () => navigate(`/tv/${eventId}`) },
                  { label: "Baixar Tudo", icon: Download, color: "#22C55E", action: () => {
                      if (event?.plan === "free") {
                        setDownloadBlockedOpen(true);
                        return;
                      }
                      const a = document.createElement("a");
                      a.href = albumsApi.downloadAll(eventId);
                      a.download = "";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    } },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className="bg-white border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-xl p-4 hover:-translate-y-1 hover:shadow-[5px_5px_0px_#1C1917] transition-all text-left"
                    data-testid={`action-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <div className="w-10 h-10 border-2 border-[#1C1917] rounded-lg flex items-center justify-center mb-2 shadow-[2px_2px_0px_#1C1917]" style={{ backgroundColor: item.color }}>
                      <item.icon className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-sm font-bold text-[#1C1917]">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Event Info */}
              <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-5">
                <h3 className="font-bold text-[#1C1917] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Detalhes do Evento</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#FFFBF7] border-2 border-[#E7E5E4] rounded-lg p-3 text-center">
                    <Users className="w-5 h-5 mx-auto text-[#8B5CF6] mb-1" strokeWidth={2.5} />
                    <p className="text-xl font-black text-[#1C1917]">{event?.guest_count || 0}</p>
                    <p className="text-xs text-[#78716C] font-bold uppercase">Convidados</p>
                  </div>
                  <div className="bg-[#FFFBF7] border-2 border-[#E7E5E4] rounded-lg p-3 text-center">
                    <Image className="w-5 h-5 mx-auto text-[#FF6B6B] mb-1" strokeWidth={2.5} />
                    <p className="text-xl font-black text-[#1C1917]">{event?.media_count || 0}</p>
                    <p className="text-xs text-[#78716C] font-bold uppercase">Midias</p>
                  </div>
                </div>
                {event?.plan === "free" && (
                  <div className="mt-4" data-testid="upload-limit-host">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-[#1C1917]">
                        {event?.media_count || 0}/{event?.free_limit || 30} midias
                      </span>
                      {(() => {
                        const used = event?.media_count || 0;
                        const limit = event?.free_limit || 30;
                        const rem = Math.max(limit - used, 0);
                        if (used >= limit) {
                          return (
                            <span className="text-[10px] font-bold uppercase text-[#FF6B6B]">
                              Limite atingido
                            </span>
                          );
                        }
                        if (rem <= 5) {
                          return (
                            <span className="text-[10px] font-bold uppercase text-[#FF6B6B]">
                              Faltam so {rem}!
                            </span>
                          );
                        }
                        return (
                          <span className="text-[10px] font-bold uppercase text-[#78716C]">
                            Gratuito
                          </span>
                        );
                      })()}
                    </div>
                    <div className="w-full h-2 bg-[#E7E5E4] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          (event?.media_count || 0) >= (event?.free_limit || 30) - 5
                            ? "bg-[#FF6B6B]"
                            : "bg-[#8B5CF6]"
                        }`}
                        style={{
                          width: `${Math.min(
                            ((event?.media_count || 0) / (event?.free_limit || 30)) * 100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Share Link */}
              <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-5">
                <h3 className="font-bold text-[#1C1917] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Compartilhar</h3>
                <div className="flex gap-2">
                  <div className="flex-1 bg-[#FFFBF7] border-2 border-[#E7E5E4] rounded-lg px-3 py-2 text-sm text-[#44403C] truncate">
                    {event?.join_url}
                  </div>
                  <Button
                    onClick={copyLink}
                    className="border-2 border-[#1C1917] rounded-lg shadow-[2px_2px_0px_#1C1917] bg-white hover:bg-[#F5F5F4]"
                    data-testid="copy-link-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Plan & Status */}
              <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-5">
                <h3 className="font-bold text-[#1C1917] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Plano & Status</h3>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase px-3 py-1 rounded-full border-2 border-[#1C1917] ${event?.plan === "premium" ? "bg-[#8B5CF6] text-white" : "bg-[#FEE2E2] text-[#1C1917]"}`}>
                    {event?.plan === "premium" ? <><Sparkles className="w-3 h-3" /> Premium</> : <><Lock className="w-3 h-3" /> Gratuito</>}
                  </span>
                  <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full border-2 border-[#1C1917] ${event?.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {event?.is_active ? "Ativo" : "Encerrado"}
                  </span>
                </div>
                {event?.plan === "free" && (
                  <p className="text-xs text-[#78716C] mb-3">
                    Aproveite ate {event?.free_limit || 30} midias gratis. Desbloqueie uploads ilimitados neste evento por apenas R$ 9,90.
                  </p>
                )}
                {event?.plan === "free" && (
                  <Button
                    onClick={() => setUpgradeModalOpen(true)}
                    className="w-full mb-3 bg-[#8B5CF6] text-white border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-full font-bold hover:translate-y-[2px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
                    data-testid="upgrade-plan-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1" /> Fazer Upgrade para Premium - R$ 9,90
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={() => setEndDialogOpen(true)}
                    disabled={ending}
                    className="flex-1 bg-red-500 text-white border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-full font-bold hover:translate-y-[2px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
                    style={{ display: event?.is_active ? undefined : "none" }}
                    data-testid="end-event-btn"
                  >
                    {ending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Power className="w-4 h-4 mr-1" /> Encerrar Evento</>}
                  </Button>
                  <Button
                    onClick={handleReactivate}
                    className="flex-1 bg-green-500 text-white border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-full font-bold hover:translate-y-[2px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
                    style={{ display: event?.is_active ? "none" : undefined }}
                    data-testid="reactivate-event-btn"
                  >
                    <Unlock className="w-4 h-4 mr-1" /> Reativar Evento
                  </Button>
                </div>
              </div>

              {/* Video Generation (Premium only) */}
              {event?.plan === "premium" && (
                <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-5">
                  <h3 className="font-bold text-[#1C1917] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Video da Festa</h3>
                  {event?.video_path ? (
                    <>
                      <p className="text-sm text-green-600 font-bold mb-3">Video gerado com sucesso!</p>
                      <video
                        key={event.video_path}
                        src={getFileUrl(event.video_path)}
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full aspect-video bg-black border-2 border-[#1C1917] rounded-lg shadow-[3px_3px_0px_#1C1917] mb-3"
                        data-testid="event-video-player"
                      >
                        Seu navegador nao suporta reproducao de video.
                      </video>
                      <a
                        href={getFileUrl(event.video_path)}
                        download={`festa-${eventId}.mp4`}
                        className="inline-flex items-center justify-center gap-2 w-full mb-2 bg-[#FFFBF7] text-[#1C1917] border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-full font-bold py-2 text-sm hover:translate-y-[1px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                        data-testid="download-video-btn"
                      >
                        <Download className="w-4 h-4" strokeWidth={2.5} />
                        Baixar Video
                      </a>
                    </>
                  ) : (
                    <p className="text-xs text-[#78716C] mb-3">Gere um video slideshow com as melhores fotos do evento.</p>
                  )}
                  <button
                    onClick={handleGenerateVideo}
                    disabled={generatingVideo}
                    className="relative w-full overflow-hidden bg-[#8B5CF6]/30 text-white border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-full font-bold h-11 transition-all disabled:cursor-wait hover:translate-y-[2px] hover:shadow-[1px_1px_0px_#1C1917] disabled:hover:translate-y-0 disabled:hover:shadow-[3px_3px_0px_#1C1917]"
                    data-testid="generate-video-btn"
                  >
                    {/* Progress fill — animates smoothly while generating */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 bg-[#8B5CF6] transition-[width] duration-500 ease-out"
                      style={{ width: generatingVideo ? `${videoProgress}%` : "100%" }}
                    />
                    {/* Diagonal stripes overlay during generation for movement */}
                    {generatingVideo && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-y-0 left-0 opacity-25"
                        style={{
                          width: `${videoProgress}%`,
                          backgroundImage:
                            "repeating-linear-gradient(45deg, rgba(255,255,255,0.4) 0 10px, rgba(255,255,255,0) 10px 20px)",
                          backgroundSize: "28px 28px",
                          animation: "festa-stripes 1s linear infinite",
                        }}
                      />
                    )}
                    {/* Label */}
                    <span className="relative flex items-center justify-center gap-2 text-sm">
                      {generatingVideo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>{videoStage ? `${videoStage} — ${Math.round(videoProgress)}%` : `Gerando video... ${Math.round(videoProgress)}%`}</span>
                        </>
                      ) : (
                        <>
                          <Film className="w-4 h-4" />
                          <span>{event?.video_path ? "Regerar Video" : "Gerar Video"}</span>
                        </>
                      )}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* QR Code Tab */}
          <TabsContent value="qrcode">
            <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-8 text-center">
              <h3 className="font-bold text-[#1C1917] mb-6 text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                QR Code do Evento
              </h3>
              {event?.qr_code && (
                <div className="inline-block p-4 bg-white border-2 border-[#1C1917] rounded-xl shadow-[4px_4px_0px_#1C1917]" data-testid="qr-code-display">
                  <img src={`data:image/png;base64,${event.qr_code}`} alt="QR Code" className="w-64 h-64" />
                </div>
              )}
              <p className="text-sm text-[#78716C] mt-4">Convidados escaneiam este codigo para entrar no evento</p>
              <p className="text-xs text-[#78716C] mt-1 font-mono">{event?.join_url}</p>
            </div>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            {stats && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "Total de Midias", value: stats.total_media, icon: Image, color: "#FF6B6B" },
                    { label: "Aprovadas", value: stats.approved_media, icon: Check, color: "#22C55E" },
                    { label: "Bloqueadas", value: stats.blocked_media, icon: BarChart3, color: "#EF4444" },
                    { label: "Total de Likes", value: stats.total_likes, icon: Heart, color: "#FF6B6B" },
                  ].map((s) => (
                    <div key={s.label} className="bg-white border-2 border-[#1C1917] shadow-[3px_3px_0px_#1C1917] rounded-xl p-4 text-center">
                      <s.icon className="w-5 h-5 mx-auto mb-1" style={{ color: s.color }} strokeWidth={2.5} />
                      <p className="text-2xl font-black text-[#1C1917]">{s.value}</p>
                      <p className="text-xs text-[#78716C] font-bold uppercase">{s.label}</p>
                    </div>
                  ))}
                </div>

                {stats.categories?.length > 0 && (
                  <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-5">
                    <h3 className="font-bold text-[#1C1917] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      Categorias Detectadas
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {stats.categories.map((cat) => (
                        <span
                          key={cat.name}
                          className="bg-[#FEE2E2] text-[#1C1917] border-2 border-[#1C1917] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                        >
                          {cat.name} ({cat.count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* End Event Confirmation - always mounted, never inside conditional render */}
      <AlertDialog open={endDialogOpen} onOpenChange={setEndDialogOpen}>
        <AlertDialogContent className="border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-black text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Encerrar Evento?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#78716C]">
              O evento sera encerrado e novos uploads serao bloqueados. Voce podera reativar a qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-2 border-[#1C1917] rounded-full font-bold shadow-[2px_2px_0px_#1C1917]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEndEvent}
              className="bg-red-500 text-white border-2 border-[#1C1917] rounded-full font-bold shadow-[2px_2px_0px_#1C1917] hover:bg-red-600"
              data-testid="confirm-end-event-btn"
            >
              Encerrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Premium Upgrade Modal - auto-opens when close to free limit */}
      <PremiumUpgradeModal
        eventId={eventId}
        mediaCount={event?.media_count || 0}
        freeLimit={event?.free_limit || 30}
        plan={event?.plan || "free"}
        open={upgradeModalOpen}
        onOpenChange={(v) => {
          // When user closes (not by paying), remember dismissal for this
          // remaining-count bucket so we don't keep popping it back up for
          // the same count.
          if (!v && event?.plan === "free") {
            const used = event?.media_count || 0;
            const limit = event?.free_limit || 30;
            const remaining = Math.max(limit - used, 0);
            try {
              localStorage.setItem(
                `festa_upgrade_reminder_${eventId}_r${remaining}`,
                "1"
              );
            } catch (_) {}
          }
          setUpgradeModalOpen(v);
        }}
      />

      {/* Albums Blocked Modal - opens when a free-plan host taps "Albums" */}
      <PremiumUpgradeModal
        eventId={eventId}
        mediaCount={event?.media_count || 0}
        freeLimit={event?.free_limit || 30}
        plan={event?.plan || "free"}
        reason="albums"
        open={albumsBlockedOpen}
        onOpenChange={setAlbumsBlockedOpen}
      />

      {/* Download Blocked Modal - opens when a free-plan host taps "Baixar Tudo" */}
      <PremiumUpgradeModal
        eventId={eventId}
        mediaCount={event?.media_count || 0}
        freeLimit={event?.free_limit || 30}
        plan={event?.plan || "free"}
        reason="download"
        open={downloadBlockedOpen}
        onOpenChange={setDownloadBlockedOpen}
      />
    </div>
  );
}
